import { useContext, createContext, useEffect, useMemo, useRef, useState } from 'react'

import { CaretUpIcon } from '@phosphor-icons/react'

import type {
  AccordionRootProps,
  AccordionContextProps,
  AccortionItemProps,
  AccordionContentProps,
  AccordionHeaderProps,
} from './accordion.type'

import { generateClassesOfIcon } from './accordion.constants'

import styles from './accordion.module.css'

const AccordionContext = createContext<AccordionContextProps>({
  activeItems: [],
  onHandleActiveItems: () => null,
})

const useAccordionContext = () => useContext(AccordionContext)

const Root = ({
  activeItems,
  children,
  onHandleActiveItems,
  className,
  showArrow = true,
}: AccordionRootProps) => {
  const contextValue = useMemo(
    () => ({ activeItems, onHandleActiveItems, showArrow }),
    [activeItems, onHandleActiveItems, showArrow],
  )

  return (
    <AccordionContext.Provider value={contextValue}>
      <ul className={`${styles['root']} ${className || ''}`}>{children}</ul>
    </AccordionContext.Provider>
  )
}

const Header = ({
  children,
  id,
  isActive,
  className,
}: AccordionHeaderProps) => {
  const { activeItems, onHandleActiveItems, showArrow } = useAccordionContext()

  const onHandleOpen = () => {
    if (isActive) {
      onHandleActiveItems(activeItems.filter((item) => item !== id))
    } else {
      onHandleActiveItems([...activeItems, id])
    }
  }

  const generatedIconClasses = generateClassesOfIcon(isActive)

  return (
    <button
      className={`${styles['header-core']} ${className || ''}`}
      onClick={onHandleOpen}
      type="button"
    >
      {children}
      {showArrow && <CaretUpIcon className={generatedIconClasses} />}
    </button>
  )
}

const Content = ({ children, isActive, className }: AccordionContentProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [maxHeight, setMaxHeight] = useState<string>('0px')

  useEffect(() => {
    if (!contentRef.current) return

    const updateHeight = () => {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          setMaxHeight(
            isActive ? `${contentRef.current.scrollHeight}px` : '0px',
          )
        }
      })
    }

    const resizeObserver = new ResizeObserver(updateHeight)
    resizeObserver.observe(contentRef.current)

    const mutationObserver = new MutationObserver(updateHeight)
    mutationObserver.observe(contentRef.current, {
      childList: true,
      subtree: true,
    })

    const timeoutId = setTimeout(updateHeight, 50)

    return () => {
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [isActive, children])

  return (
    <div
      ref={contentRef}
      className={`${styles['content-core']} ${className || ''}`}
      style={{ maxHeight }}
    >
      {children}
    </div>
  )
}

const Item = ({
  id,
  children,
  className,
  header,
  footer,
  contentClassName,
  headerClassName,
}: AccortionItemProps) => {
  const context = useAccordionContext()

  const isActive = context?.activeItems?.includes(id)

  return (
    <li className={className}>
      <Header isActive={isActive} id={id} className={headerClassName}>
        {header}
      </Header>

      <Content isActive={isActive} className={contentClassName}>
        {children}
      </Content>

      {footer}
    </li>
  )
}

export const Accordion = {
  Root,
  Item,
}